/**
 * Zod schema for the "Create Policy" form (F039 Autonomous Governance) — Doc 69 Layer 2 output,
 * imported by `ResponsePolicyForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs
 * `VP-01`..`VP-12` mirror the verbatim messages previously hand-rolled in
 * `responsePolicyFormFields.tsx`.
 */
import { z } from 'zod';
import type {
  CreateResponsePolicyInput,
  ResponseActionType,
  ResponseImpactClass,
  ResponseSeverity,
  ResponseTriggerKind,
} from '../../../types';
import {
  RESPONSE_TRIGGER_KIND_OPTIONS,
  RESPONSE_SEVERITY_OPTIONS,
  RESPONSE_ACTION_TYPE_OPTIONS,
  RESPONSE_IMPACT_CLASS_OPTIONS,
} from './responsePolicyFormFields';

// reason: the *_OPTIONS arrays are runtime-verified non-empty arrays of their union type; zod's
// z.enum() requires a literal non-empty tuple type.
const TRIGGER_TUPLE = RESPONSE_TRIGGER_KIND_OPTIONS as [ResponseTriggerKind, ...ResponseTriggerKind[]];
const SEVERITY_TUPLE = RESPONSE_SEVERITY_OPTIONS as [ResponseSeverity, ...ResponseSeverity[]];
const ACTION_TUPLE = RESPONSE_ACTION_TYPE_OPTIONS as [ResponseActionType, ...ResponseActionType[]];
const IMPACT_TUPLE = RESPONSE_IMPACT_CLASS_OPTIONS as [ResponseImpactClass, ...ResponseImpactClass[]];

/** Dropdown fields that start unselected (`''`) — required-with-verbatim-message via a
 *  union-with-empty-literal + type-predicate refine, so the schema's OUTPUT type narrows to the
 *  real union once non-empty (no `''` ever reaches the request DTO). */
function requiredEnum<T extends string>(options: readonly [T, ...T[]], message: string) {
  return z.union([z.enum(options), z.literal('')]).refine((v): v is T => v !== '', { message });
}

export const ResponsePolicyCreateSchema = z.object({
  // VP-01 (required @ submit) / VP-02 (max 200 @ blur)
  externalId: z
    .string()
    .trim()
    .min(1, 'External id is required')
    .max(200, 'External id must be 200 characters or fewer'),
  // VP-03 / VP-04
  name: z.string().trim().min(1, 'Name is required').max(200, 'Name must be 200 characters or fewer'),
  // VP-05 (optional, max 2000)
  description: z
    .string()
    .max(2000, 'Description must be 2000 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // VP-06 (required — starts unselected)
  appliesToTrigger: requiredEnum(TRIGGER_TUPLE, 'Select which trigger this policy applies to'),
  // VP-07 (required — starts unselected)
  minSeverity: requiredEnum(SEVERITY_TUPLE, 'Select a minimum severity'),
  // VP-08 (required) / VP-09 (max 2000)
  conditionExpression: z
    .string()
    .trim()
    .min(1, 'Condition expression is required')
    .max(2000, 'Condition expression must be 2000 characters or fewer'),
  // VP-10 (required — starts unselected)
  action: requiredEnum(ACTION_TUPLE, 'Select a response action'),
  // Optional — "No override" is the unselected state.
  impactOverride: z.union([z.enum(IMPACT_TUPLE), z.literal('')]).transform((v) => (v === '' ? undefined : v)),
  // VP-11 (required, whole number)
  priority: z
    .string()
    .trim()
    .min(1, 'Priority is required')
    .refine((v) => /^-?\d+$/.test(v), { message: 'Priority must be a whole number' })
    .transform((v) => Number(v)),
  // VP-12 (optional, hh:mm:ss when non-empty)
  debounceWindow: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === '' || /^\d{2}:\d{2}:\d{2}$/.test(v), {
      message: 'Debounce window must be in hh:mm:ss format, e.g. 00:05:00',
    })
    .transform((v) => v || undefined),
});

export type ResponsePolicyCreateFormValues = z.input<typeof ResponsePolicyCreateSchema>;
export type ResponsePolicyCreateFormOutput = z.output<typeof ResponsePolicyCreateSchema>;

// Compile-time proof the schema's output assembles a valid A-01 request DTO.
type _AssertAssignable = ResponsePolicyCreateFormOutput extends CreateResponsePolicyInput ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
